import React, { forwardRef } from 'react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  leftElement?: React.ReactNode;
  rightElement?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, leftElement, rightElement, className = '', ...props }, ref) => {
    // Detect if a compact height class is used (h-8, h-9, h-10)
    const isCompactHeight = /\bh-[89]|h-10\b/.test(className);
    const paddingClasses = isCompactHeight ? 'px-3 py-1.5' : 'px-4 py-3';
    const textClasses = isCompactHeight ? 'text-sm' : 'text-base leading-[1.75em]';

    return (
      <div className="w-full font-['Inter',sans-serif]">
        {label && (
          <label className="block text-base font-medium text-[#1F1F1F] mb-2 leading-[1.75em]">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftElement && (
            <div className="absolute left-0 top-0 h-full flex items-center">{leftElement}</div>
          )}
          <input
            ref={ref}
            className={`w-full ${paddingClasses} border rounded-lg focus:ring-2 focus:outline-none transition-all duration-200 ${textClasses} font-normal ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]'
            } ${
              props.disabled
                ? 'bg-gray-50 text-[#6E7077] cursor-not-allowed'
                : 'bg-white hover:border-[var(--brand-primary)] text-[#1F1F1F]'
            } ${leftElement ? 'pl-12' : ''} ${rightElement ? 'pr-12' : ''} focus:ring-opacity-20 placeholder-[#6E7077] ${className}`}
            {...props}
          />
          {rightElement && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              {rightElement}
            </div>
          )}
        </div>
        {error && <p className="mt-2 text-sm text-red-600 font-normal leading-[1.75em]">{error}</p>}
        {hint && !error && (
          <p className="mt-2 text-sm text-[#6E7077] font-normal leading-[1.75em]">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
